import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Gathers comprehensive client session information for forestry and UX optimization.
 */
const getClientSessionInfo = () => {
    if (typeof window === 'undefined') return {};
    
    return {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        vendor: navigator.vendor,
        language: navigator.language,
        languages: navigator.languages,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        colorDepth: window.screen.colorDepth,
        orientation: window.screen.orientation?.type || 'unknown',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

        timezoneOffset: new Date().getTimezoneOffset(),
        // Chrome-only APIs — undefined elsewhere, and Firestore rejects
        // undefined field values (every security log silently failed on
        // Safari/Firefox until 2026-07-26). null is storable; undefined is not.
        hardwareConcurrency: navigator.hardwareConcurrency ?? null,
        deviceMemory: navigator.deviceMemory ?? null, // Approx. RAM in GB
        connectionType: navigator.connection?.effectiveType || 'unknown',
        isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        referrer: document.referrer
    };
};

/**
 * Security Logs - Tracks authentication and authorization boundaries
 */
export const logSecurityEvent = async (userOrEmail, actionType, details = {}) => {
    try {
        const uid = userOrEmail?.uid || 'anonymous';
        const email = typeof userOrEmail === 'string' ? userOrEmail : (userOrEmail?.email || 'unknown');

        await addDoc(collection(db, 'security_logs'), {
            userId: uid,
            userEmail: email,
            actionType,
            timestamp: serverTimestamp(),
            details,
            clientInfo: getClientSessionInfo()
        });
    } catch (err) {
        console.error("Failed to write security log:", err);
    }
};

/**
 * Audit Logs - Tracks "who did what and when" regarding core application data
 *
 * options.recordWhileViewingAs (v3.98.1): audit events are normally skipped
 * while an admin views the app as another user (noise). A caller that must be
 * on record even then (corrections of saved results — Kemal, 2026-11-02) sets
 * this flag; the entry is then written with viewingAs: true. The session is
 * signed in as the viewed user and the token does not name the admin, so
 * userId / userEmail are the viewed user's: viewingAs says the change may
 * have been made by an admin viewing as them.
 */
export const logAuditEvent = async (user, actionType, targetId, details = {}, options = {}) => {
    try {
        const viewingAs = typeof window !== 'undefined' && window.sessionStorage.getItem('isImpersonating') === 'true';
        // Skip logging for audit events during admin impersonation to reduce noise
        if (viewingAs && !(options && options.recordWhileViewingAs)) {
            return;
        }

        if (!user || (!user.uid && !user.email)) {
            console.warn("Logger: Unknown user. Cannot log reliably.");
            return;
        }

        await addDoc(collection(db, 'audit_logs'), {
            userId: user.uid || 'unknown',
            userEmail: user.email || 'unknown',
            actionType,
            targetId,
            timestamp: serverTimestamp(),
            details,
            ...(viewingAs ? { viewingAs: true } : {}),
            clientInfo: getClientSessionInfo()
        });
    } catch (err) {
        console.error("Failed to write audit log:", err);
    }
};

// Aliased for backward compatibility with existing codebase
export const logAudit = logAuditEvent;

/**
 * Application / Error Logs - Tracks UI crashes and unexpected states
 */
export const logError = async (user, errorType, errorMessage, stackTrace = "", details = {}) => {
    try {
        // Skip logging for errors during admin impersonation to reduce noise
        if (typeof window !== 'undefined' && window.sessionStorage.getItem('isImpersonating') === 'true') {
            console.warn("Logger: Suppressing error log during impersonation session.");
            return;
        }

        const uid = user?.uid || 'anonymous';
        const email = user?.email || 'unknown';

        await addDoc(collection(db, 'error_logs'), {
            userId: uid,
            userEmail: email,
            actionType: 'ERROR',
            errorType,
            errorMessage,
            stackTrace,
            timestamp: serverTimestamp(),
            details,
            clientInfo: getClientSessionInfo()
        });
    } catch (err) {
        console.error("Failed to write error log:", err);
    }
};

